import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";

/**
 * H1_Desc_5 Component
 * Displays a heading (H1), multiple description paragraphs, a coupon code block,
 * a CTA button, and closing signature text
 * Centered layout with clean, simple design
 */
const H1Desc5 = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "",
    couponInstruction = "",
    couponCode = "",
    buttonText = "",
    buttonLink = "#",
    closingText = "",
    component_name = "H1_Desc_5",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localCouponInstruction, setLocalCouponInstruction] = useState(couponInstruction);
  const [localCouponCode, setLocalCouponCode] = useState(couponCode);
  const [localButtonText, setLocalButtonText] = useState(buttonText);
  const [localButtonLink, setLocalButtonLink] = useState(buttonLink);
  const [localClosingText, setLocalClosingText] = useState(closingText);

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalDescription(description || "");
    setLocalCouponInstruction(couponInstruction || "");
    setLocalCouponCode(couponCode || "");
    setLocalButtonText(buttonText || "");
    setLocalButtonLink(buttonLink || "#");
    setLocalClosingText(closingText || "");
  }, [id, title, description, couponInstruction, couponCode, buttonText, buttonLink, closingText]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
      couponInstruction: localCouponInstruction,
      couponCode: localCouponCode,
      buttonText: localButtonText,
      buttonLink: localButtonLink,
      closingText: localClosingText,
    });
    setIsEditing(false);
  };

  // Helper function to split description into paragraphs
  const getParagraphs = (text) => {
    if (!text) return [];
    // Split by double newlines
    const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());
    if (paragraphs.length === 0 && text.trim()) {
      return text.split(/\n/).filter((p) => p.trim());
    }
    return paragraphs;
  };

  // Helper function to parse bold text (**text**)
  const parseBoldText = (text) => {
    if (!text) return text;
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  // Helper function to split closing text into lines
  const getClosingLines = (text) => {
    if (!text) return [];
    return text.split(/\n/).filter((line) => line.trim());
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component h1-desc-5-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Heading & Description with Coupon Code</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Heading (H1)</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter heading (e.g., Hey *|FNAME|*!)"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description. Use **text** for bold. Use double line breaks to separate paragraphs."
            rows={4}
          />
          <small className="text-muted">
            Tip: Use **text** for bold text. Use double line breaks to create separate paragraphs.
          </small>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Coupon Instruction Text</label>
          <input
            className="form-control"
            value={localCouponInstruction}
            onChange={(e) => setLocalCouponInstruction(e.target.value)}
            placeholder="Enter instruction text for coupon code"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Coupon Code</label>
          <input
            className="form-control"
            value={localCouponCode}
            onChange={(e) => setLocalCouponCode(e.target.value)}
            placeholder="Enter coupon code"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Text</label>
          <input
            className="form-control"
            value={localButtonText}
            onChange={(e) => setLocalButtonText(e.target.value)}
            placeholder="Enter button text"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Link</label>
          <input
            className="form-control"
            value={localButtonLink}
            onChange={(e) => setLocalButtonLink(e.target.value)}
            placeholder="Enter button link URL"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Closing Text</label>
          <textarea
            className="form-control"
            value={localClosingText}
            onChange={(e) => setLocalClosingText(e.target.value)}
            placeholder="Enter closing text. Use line breaks to separate lines."
            rows={3}
          />
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const paragraphs = getParagraphs(localDescription);
  const closingLines = getClosingLines(localClosingText);

  return (
    <>
      {!previewMode && (
        <div className="email-component h1-desc-5 mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tbody>
          <tr>
            <td
              align="center"
              style={{
                padding: "32px 16px",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <table
                width="100%"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{
                  maxWidth: "600px",
                  margin: "0 auto",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                <tbody>
                  {/* Heading (H1) */}
                  {localTitle && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          paddingBottom: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <h1
                          style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            color: primaryColor || "#333333",
                            margin: "0 0 16px 0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            textAlign: "center",
                          }}
                        >
                          {localTitle}
                        </h1>
                      </td>
                    </tr>
                  )}

                  {/* Description Paragraphs */}
                  {paragraphs.length > 0 &&
                    paragraphs.map((paragraph, index) => (
                      <tr key={index}>
                        <td
                          align="left"
                          style={{
                            paddingBottom: "16px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              color: primaryColor || "#333333",
                              lineHeight: "1.6",
                              margin: "0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              textAlign: "left",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: parseBoldText(paragraph.trim()),
                            }}
                          />
                        </td>
                      </tr>
                    ))}

                  {/* Coupon Code Block */}
                  {(localCouponInstruction || localCouponCode) && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "24px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          width="100%"
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            border: `2px solid ${secondaryColor || "#a1894c"}`,
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            padding: "20px",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tbody>
                            {localCouponInstruction && (
                              <tr>
                                <td
                                  align="center"
                                  style={{
                                    paddingBottom: localCouponCode ? "12px" : "0",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: "16px",
                                      color: primaryColor || "#333333",
                                      lineHeight: "1.6",
                                      margin: "0",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                      textAlign: "center",
                                    }}
                                  >
                                    {localCouponInstruction}
                                  </p>
                                </td>
                              </tr>
                            )}
                            {localCouponCode && (
                              <tr>
                                <td
                                  align="center"
                                  style={{
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "24px",
                                      fontWeight: "bold",
                                      color: secondaryColor || "#a1894c",
                                      letterSpacing: "1px",
                                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                    }}
                                  >
                                    {localCouponCode}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* CTA Button */}
                  {localButtonText && (
                    <tr>
                      <td
                        align="center"
                        style={{
                          padding: "24px 0",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{ display: "inline-block" }}
                        >
                          <tr>
                            <td
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "4px",
                                border: `2px solid ${secondaryColor || "#a1894c"}`,
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <a
                                href={localButtonLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "block",
                                  padding: "12px 24px",
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: secondaryColor || "#a1894c",
                                  textDecoration: "none",
                                  fontFamily: fontFamily || "Arial, Helvetica, sans-serif",
                                }}
                              >
                                {localButtonText}
                              </a>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  )}

                  {/* Closing Text */}
                  {closingLines.length > 0 && (
                    <tr>
                      <td
                        align="left"
                        style={{
                          paddingTop: "16px",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {closingLines.map((line, index) => (
                          <p
                            key={index}
                            style={{
                              fontSize: "16px",
                              color: primaryColor || "#333333",
                              lineHeight: "1.6",
                              margin: index < closingLines.length - 1 ? "0 0 8px 0" : "0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              textAlign: "left",
                            }}
                          >
                            {line.trim()}
                          </p>
                        ))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default H1Desc5;

